import api from "../../../api/axiosInstance";
import { displayToast } from "../toast/actions";

const SYSTEM_SESSION_ID_KEY = "systemSessionId";

export const setLoading = (isLoading) => {
  return async (dispatch) => {
    dispatch({ type: "SET_LOADING", payload: isLoading });
  };
};

export const setDefaultSteps = (object) => {
  return async (dispatch) => {
    dispatch({ type: "SET_DEFAULT_STEPS", payload: object });
  };
};

export const updateSystemSessionApplicationName = (mainMenu, subMenu = null) => {
  return async () => {
    try {
      const sessionId = sessionStorage.getItem(SYSTEM_SESSION_ID_KEY);
      if (!sessionId) {
        return;
      }

      const normalizedMainMenu = (mainMenu || "").trim();
      const normalizedSubMenu = (subMenu || "").trim();
      const applicationName = normalizedSubMenu
        ? `${normalizedMainMenu} - ${normalizedSubMenu}`
        : normalizedMainMenu;

      if (!applicationName) {
        return;
      }

      await api.post("/api/system-access/session/application", {
        sessionId,
        applicationName,
      });
    } catch (error) {
      // Keep logging best-effort and non-blocking for clinical workflow.
    }
  };
};

export const resetState = () => {
  return async (dispatch) => {
    dispatch({ type: "RESET_STATE" });
  };
};

export const fetchClinicalServices = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/clinicalServices/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_CLINICAL_SERVICES_SUCCESS", payload: data });
    } catch (error) {
      dispatch({
        type: "FETCH_CLINICAL_SERVICES_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchClinicalService = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/initialAssessment/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_CLINICAL_SERVICE_SUCCESS", payload: data });
      dispatch({ type: "SET_CLINICAL_SERVICE_STEPS_SUCCESS", payload: data?.initialAssessment?.initialAssessmentForm?.serviceSteps });
    } catch (error) {
      dispatch({
        type: "FETCH_CLINICAL_SERVICE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const createClinicalService = (id, code, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(
        `/initialAssessment/${id}/${code}`,
        object
      );
      const data = response.data;
      dispatch({ type: "CREATE_CLINICAL_SERVICE_SUCCESS", payload: data });
      dispatch({ type: "SET_CLINICAL_SERVICE_NAME_SUCCESS", payload: code });
      dispatch(fetchClinicalServices(id));
    } catch (error) {
      dispatch({
        type: "CREATE_CLINICAL_SERVICE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const updateClinicalService = (id, object, showToast = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/initialAssessment/${id}`, object);
      const data = response.data;
      dispatch({ type: "UPDATE_CLINICAL_SERVICE_SUCCESS", payload: data });
      if(showToast) {
        dispatch(displayToast('Referral Update Complete', 'success', {autoClose:4000}));
      }
    } catch (error) {
      dispatch({
        type: "UPDATE_CLINICAL_SERVICE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const deleteClinicalService = (patientId, serviceId, navigate) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/initialAssessment/${serviceId}`);
      const data = response.data;
      dispatch({ type: "DELETE_CLINICAL_SERVICE_SUCCESS", payload: serviceId });
      navigate(`/patient/${patientId}/clinical-services/`);
    } catch (error) {
      dispatch({
        type: "DELETE_CLINICAL_SERVICE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const setClinicalServiceName = (serviceTypeCode) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch({
        type: "SET_CLINICAL_SERVICE_NAME_SUCCESS",
        payload: serviceTypeCode,
      });
    } catch (error) {
      dispatch({
        type: "SET_CLINICAL_SERVICE_NAME_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const setClinicalServiceSteps = (serviceSteps) => {
  let updatedServiceSteps = serviceSteps;
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch({
        type: "SET_CLINICAL_SERVICE_STEPS_SUCCESS",
        payload: updatedServiceSteps,
      });
    } catch (error) {
      dispatch({
        type: "SET_CLINICAL_SERVICE_STEPS_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchMedicationReview = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(
        `/annualMedicationReview/initialAssessment/${id}`
      );
      const data = response.data;
      dispatch({ type: "FETCH_MEDICATION_REVIEW_SUCCESS", payload: data });
      dispatch({ type: "SET_CLINICAL_SERVICE_STEPS_SUCCESS", payload: data?.initialAssessment?.initialAssessmentForm?.serviceSteps });
    } catch (error) {
      dispatch({
        type: "FETCH_MEDICATION_REVIEW_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const createMedicationReview = (patientId, code, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(
        `/annualMedicationReview/initialAssessment/${patientId}`,
        object
      );
      const data = response.data;
      dispatch({ type: "CREATE_MEDICATION_REVIEW_SUCCESS", payload: data });
      dispatch({ type: "SET_CLINICAL_SERVICE_NAME_SUCCESS", payload: code });
      dispatch(fetchClinicalServices(patientId));
    } catch (error) {
      dispatch({
        type: "CREATE_MEDICATION_REVIEW_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const updateMedicationReview = (id, object, showToast = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      
      // Extract profileAnalysisClicked from request before API call
      const profileAnalysisClickedValue = object?.medications?.profileAnalysisClicked;
      const requestVaccineSchedule = Array.isArray(object?.education?.vaccineSchedule)
        ? object.education.vaccineSchedule : null;
      
      const response = await api.put(`/annualMedicationReview/initialAssessment/${id}`, object);
      const data = response.data;

      // Preserve rich vaccine row metadata from request when backend returns reduced schedule shape.
      if (
        requestVaccineSchedule &&
        data?.initialAssessment?.initialAssessmentForm?.education
      ) {
        const responseSchedule = Array.isArray(
          data.initialAssessment.initialAssessmentForm.education.vaccineSchedule
        )
          ? data.initialAssessment.initialAssessmentForm.education.vaccineSchedule : [];
        const toVaccineName = (vaccine) => {
          if (typeof vaccine === "string" || typeof vaccine === "number") {
            return String(vaccine).trim();
          }
          const nameValue = vaccine?.name ?? vaccine?.value ?? vaccine?.label;
          if (typeof nameValue === "string") return nameValue.trim();
          if (!nameValue || typeof nameValue !== "object") return "";
          const candidate =
            nameValue?.name ??
            nameValue?.value ??
            nameValue?.label ??
            nameValue?.name?.name ??
            nameValue?.name?.value ??
            nameValue?.name?.label;
          return typeof candidate === "string" ? candidate.trim() : "";
        };
        const requestScheduleByKey = new Map(
          requestVaccineSchedule.map((item) => [
            item?.id ?? toVaccineName(item),
            item,
          ])
        );
        data.initialAssessment.initialAssessmentForm.education.vaccineSchedule =
          responseSchedule.map((item) => {
            const key = item?.id ?? toVaccineName(item);
            const requestItem = requestScheduleByKey.get(key) || {};
            return {
              ...requestItem,
              ...item,
              name: toVaccineName(item) || toVaccineName(requestItem),
            };
          });
      }
      
      // Preserve profileAnalysisClicked from request if backend doesn't return it
      if (profileAnalysisClickedValue !== undefined && 
          data.initialAssessment?.initialAssessmentForm?.medications?.profileAnalysisClicked === undefined) {
        data.initialAssessment.initialAssessmentForm.medications.profileAnalysisClicked = profileAnalysisClickedValue;
      }
      
      dispatch({ type: "UPDATE_MEDICATION_REVIEW_SUCCESS", payload: data });
      if(showToast) {
        dispatch(displayToast('Referral Update Complete', 'success', {autoClose:4000}));
      }
    } catch (error) {
      dispatch({
        type: "UPDATE_MEDICATION_REVIEW_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const deleteMedicationReview = (patientId, serviceId, navigate) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/medicationReview/${serviceId}`);
      const data = response.data;
      dispatch({ type: "DELETE_MEDICATION_REVIEW_SUCCESS", payload: serviceId });
      navigate(`/patient/${patientId}/clinical-services/`);
    } catch (error) {
      dispatch({
        type: "DELETE_MEDICATION_REVIEW_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchMedicalConditions = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/medicalCondition/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_MEDICAL_CONDITIONS_SUCCESS", payload: data });
    } catch (error) {
      dispatch({
        type: "FETCH_MEDICAL_CONDITIONS_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const createMedicalCondition = (id, object, fromClinicalService = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let url = `/patient/medicalCondition/${id}`;
      if (fromClinicalService) {
        url += '?skipClinicalCheck=true';
      }
      const response = await api.post(url, object);
      const data = response.data;
      dispatch({ type: "CREATE_MEDICAL_CONDITION_SUCCESS", payload: data });
      
      // Show success toast for patient chart additions and trigger auto-checking
      if (!fromClinicalService) {
        dispatch(displayToast("Medical condition added successfully", "success", { autoClose: 4000 }));
        // Trigger auto-checking in clinical services
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('medicalConditionAdded', { 
            detail: { condition: data.medicalConditions?.[0] } 
          }));
        }, 100);
      }
    } catch (error) {
      dispatch({
        type: "CREATE_MEDICAL_CONDITION_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateMedicalConditionFromChart = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/medicalCondition/${id}`, object);
      const condition = object.medicalConditions[0];
      const conditionId = condition?.id;
      const isDeletion = condition?.status === "inactive";
      const isRestoration =
        condition?.status === "active" && condition?.previousStatus === "inactive";
      const updatedCondition = response.data?.medicalConditions?.find(val => val.id === conditionId);
      
      if (updatedCondition?.id) {
        dispatch({ type: "UPDATE_MEDICAL_CONDITION_SUCCESS_FROM_CHART", payload: updatedCondition });
      } else if (isDeletion && conditionId) {
        dispatch({ type: "DELETE_MEDICAL_CONDITION_SUCCESS", payload: conditionId });
      }

      if (isDeletion) {
        dispatch(displayToast('Medical condition has been deleted successfully.', 'success', {autoClose:4000}));
      } else if (isRestoration) {
        dispatch(displayToast('Medical Condition restored successfully', 'success', {autoClose:4000}));
      } else {
        dispatch(displayToast('Medical condition has been updated successfully.', 'success', {autoClose:4000}));
      }
      
      await dispatch(fetchMedicalConditions(id));
    } catch (error) {
      dispatch({
        type: "UPDATE_MEDICAL_CONDITION_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateMedicalCondition = (id, object, fromClinicalService = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const url = fromClinicalService 
        ? `/patient/medicalCondition/${id}?skipClinicalCheck=true`
        : `/patient/medicalCondition/${id}`;
      
      const response = await api.post(url, object);
      dispatch({ type: "UPDATE_MEDICAL_CONDITION_SUCCESS", payload: response.data });
      
      // Sync updated conditions to patient reducer for instant form updates
      await dispatch(fetchMedicalConditions(id));
    } catch (error) {
      dispatch({
        type: "UPDATE_MEDICAL_CONDITION_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const deleteMedicalCondition = (patientId, id, fromClinicalService = true) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let url = `/medicalCondition/${patientId}/${id}`;
      if (fromClinicalService) {
        url += '?skipClinicalCheck=true';
      }
      const response = await api.delete(url);
      dispatch({ type: "DELETE_MEDICAL_CONDITION_SUCCESS", payload: id });
    } catch (error) {
      dispatch({
        type: "DELETE_MEDICAL_CONDITION_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchMedications = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/medication/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_MEDICATIONS_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "FETCH_MEDICATIONS_FAILURE", payload: error.message });
    }
  };
};

export const createMedication = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/medication/${id}`, object);
      const data = response.data;
      dispatch({ type: "CREATE_MEDICATION_SUCCESS", payload: data });
      return data
    } catch (error) {
      dispatch({ type: "CREATE_MEDICATION_FAILURE", payload: error.message });
    }
  };
};

export const updateMedication = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/medication/${id}`, object);
      const data = response.data;
      dispatch({ type: "UPDATE_MEDICATION_SUCCESS", payload: data });
      return data;
    } catch (error) {
      dispatch({ type: "UPDATE_MEDICATION_FAILURE", payload: error.message });
    }
  };
}

export const savePatientMedications = (patientId, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/medication/${patientId}`, object);
      const data = response.data;
      dispatch({ type: "UPDATE_MEDICATION_SUCCESS", payload: data });
      return data;
    } catch (error) {
      dispatch({ type: "UPDATE_MEDICATION_FAILURE", payload: error.message });
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        "Unable to save vaccine medications.";
      dispatch(displayToast(message, "error", { autoClose: false }));
      throw error;
    }
  };
};

export const deleteMedication = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/medication/${id}`);
      dispatch({ type: "DELETE_MEDICATION_SUCCESS", payload: id });
    } catch (error) {
      dispatch({ type: "DELETE_MEDICATION_FAILURE", payload: error.message });
    }
  };
};

//TODO: Implement delete medication
export const purgeClinicalServices = () => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      await api.delete(`/clinicalServices/purge`);
    } catch (error) {
      console.log(error)
    }
  };
};

export const updateMinorAilmentStatus = (minorAilmentId, status, rxDetails) => {
  return async (dispatch) => {
    try {
      const response = await api.put(`/minorAilment/` + minorAilmentId, {
        "status": status,
        "rxDetails": rxDetails
      });
      dispatch({ type: "UPDATE_MINOR_AILMENT_SUCCESS", payload: response.data });
    } catch (error) {
      console.log(error)
      dispatch({
        type: "UPDATE_MINOR_AILMENT_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const updateMedicalReviewStatus = (medicalReviewId, status) => {
  return async (dispatch) => {
    try {
      await api.put(`/medicationReview/` + medicalReviewId, {
        "status": status
      });
      let data= {medReviewId: medicalReviewId, medReviewStatus: status};
      dispatch({ type: "UPDATE_STATUS_MEDICATION_REVIEW_SUCCESS", payload: data });
    } catch (error) {
      console.log(error)
      dispatch({
        type: "UPDATE_STATUS_MEDICATION_REVIEW_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchDrugs = (query, filter, returnSize = 300) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let results = await api.get(`/drugs?search=${query}&filter=${filter}&returnSize=${returnSize} `, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return results.data;
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
    }
  }
};

export const fetchDrugsCustom = (customEndPoint = '/drugs', query, filter = 'All', returnSize = 100, strength = null) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const strengthParam = strength ? `&strength=${encodeURIComponent(strength)}` : '';
      let results = await api.get(`${customEndPoint}?search=${query}&filter=${filter}&returnSize=${returnSize}${strengthParam} `, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return results.data;
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
    }
  }
};

export const fetchMedicalHistory = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/medicalHistory/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_MEDICAL_HISTORY_SUCCESS", payload: data });
    } catch (error) {
      dispatch({
        type: "FETCH_MEDICAL_HISTORY_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const searchMedicalConditions = (query) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let results = await api.get(`/medicalCondition/search?search=${query}`, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return results.data || [];
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
      return [];
    }
  };
};

export const searchReactions = (query) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let results = await api.get(`/symptom/search?search=${query}`, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return results.data;
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
    }
  };
};

export const createMedicalHistory = (id, object, isFromPatientBar = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/medicalHistory/${id}`, object);
      dispatch({ type: "CREATE_MEDICAL_HISTORY_SUCCESS", payload: response.data });
      // Sync updated conditions to patient reducer for instant form updates - only fetch if medical conditions were actually created/updated
      if (object.medicalConditions?.length > 0) {
        await dispatch(fetchMedicalConditions(id));
      }
      
      // Show toast for patient chart additions
      if (isFromPatientBar) {
        dispatch(displayToast('Medical condition added successfully', 'success', { autoClose: 4000 }));
        
        // Trigger auto-checking in clinical services
        setTimeout(() => {
          const newCondition = response.data?.medicalConditions?.[0];
          if (newCondition) {
            window.dispatchEvent(new CustomEvent('medicalConditionAdded', { 
              detail: { condition: newCondition } 
            }));
          }
        }, 100);
      }
    } catch (error) {
      dispatch({
        type: "CREATE_MEDICAL_HISTORY_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateMedicalHistory = (id, object, showToast = false) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/patient/medicalHistory/${id}`, object);
      dispatch({ type: "UPDATE_MEDICAL_HISTORY_SUCCESS", payload: response.data });
      // Sync updated conditions to patient reducer for instant form updates - only fetch if medical conditions were actually created/updated
      if (object.medicalConditions?.length > 0) {
        await dispatch(fetchMedicalConditions(id));
      }
      
      if (showToast) {
        // showToast can be boolean true (default 'updated') or a string indicating specific toast type: 'deleted' | 'restored' | 'updated' | 'added'
        let message = 'Medical condition has been updated successfully.';
        if (showToast === 'deleted') {
          message = 'Medical condition has been deleted successfully.';
        } else if (showToast === 'restored') {
          message = 'Medical Condition restored successfully';
        } else if (showToast === 'added') {
          message = 'Medical condition added successfully';
        }
        dispatch(displayToast(message, 'success', { autoClose: 4000 }));
      }
    } catch (error) {
      dispatch({
        type: "UPDATE_MEDICAL_HISTORY_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const deleteMedicalSurgery = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/surgery/${id}`);
      const data = response.data;
      dispatch({ type: "DELETE_SURGERY_SUCCESS", payload: id });
    } catch (error) {
      dispatch({
        type: "DELETE_SURGERY_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};